import { FC, useCallback, useMemo, useState } from 'react'
import {
  createBoxVariantComponents,
  createButtonVariantComponents,
  createMostlyFluidVariantComponents
} from '../setup-factories'
import styled from 'styled-components/macro'

const { BoxContainer } = createBoxVariantComponents({
  Container: {
    position: 'relative',
    margin: { top: '0', right: '0', bottom: '24px', left: '0' },
    width: '100%',
    background: { color: '#EBE2D5' },
    breakpoints: {
      large: { margin: { top: '0', right: '0', bottom: '15px', left: '0' } },
      medium: { margin: { top: '0', right: '0', bottom: '10px', left: '0' } },
      small: { margin: { top: '0', right: '0', bottom: '8px', left: '0' } }
    }
  }
})
const StyledBoxContainer = styled(BoxContainer)<{ $backgroundColor: string }>`
  overflow: hidden;
  background-color: ${(props) => props.$backgroundColor};
`
const Image = styled.img`
  width: 100%;
  display: block;
`

const { MostlyFluidThumbs } = createMostlyFluidVariantComponents({
  Thumbs: {
    horizontalPadding: '24px',
    breakpoints: {
      large: { horizontalPadding: '15px' },
      medium: { horizontalPadding: '10px' },
      small: { horizontalPadding: '8px' }
    }
  }
})
const StyledMostlyFluidThumbs = styled(MostlyFluidThumbs)`
  margin-bottom: 50px;
  > * {
    flex: 0 1 33.333%;
    max-width: 151px;
  }
  @media (max-height: 720px) {
    margin-bottom: 25px;
  }
`
const { BoxThumb } = createBoxVariantComponents({
  Thumb: {
    background: { color: '#EBE2D5' },
    alignItems: 'flex-end'
  }
})
const StyledBoxThumb = styled(BoxThumb)<{ $backgroundColor: string }>`
  position: relative;
  background-color: ${(props) => props.$backgroundColor};
`
const { ButtonThumbAction } = createButtonVariantComponents({
  ThumbAction: {
    font: {},
    background: { color: 'transparent' },
    $when: {
      active: {},
      disabled: {},
      focus: {},
      hover: {}
    }
  }
})
const StyledButtonThumbAction = styled(ButtonThumbAction)`
  opacity: 1;
  transition: opacity 0.2s;
  &:hover {
    opacity: 0.8;
  }
`
const { BoxLine } = createBoxVariantComponents({
  Line: {
    position: { position: 'absolute', top: 'calc(100% + 8px)' },
    background: { color: '#02BAB5' },
    height: '4px',
    width: '100%'
  }
})

interface ProductBackgroundOptionProps {
  backgroundOption: string
  selectedBackground: string
  selectedColorOption: Window['colorConfiguratorColorOptions'][number] | undefined
  onSelect: (backgroundOption: string) => void
}

const ProductBackgroundOption: FC<ProductBackgroundOptionProps> = ({
  backgroundOption,
  selectedBackground,
  selectedColorOption,
  onSelect
}) => {
  const onClickBackground = useCallback(() => onSelect(backgroundOption), [backgroundOption, onSelect])

  return (
    <StyledBoxThumb onClick={onClickBackground} $backgroundColor={selectedColorOption?.hexCode ?? '#EBE2D5'}>
      <StyledButtonThumbAction type="button">
        <Image src={backgroundOption} alt="" />
      </StyledButtonThumbAction>
      {backgroundOption === selectedBackground && <BoxLine />}
    </StyledBoxThumb>
  )
}

interface ProductViewerProps {
  selectedColorOption: Window['colorConfiguratorColorOptions'][number] | undefined
}

export const ProductViewer: FC<ProductViewerProps> = ({ selectedColorOption }) => {
  const backgroundOptions = useMemo(() => window.colorConfiguratorBackgrounds, [])
  const [background, setBackground] = useState(backgroundOptions[0]!)

  return (
    <>
      <StyledBoxContainer $backgroundColor={selectedColorOption?.hexCode ?? '#EBE2D5'}>
        <Image src={background} alt="" />
      </StyledBoxContainer>
      <StyledMostlyFluidThumbs>
        {backgroundOptions.map((backgroundOption) => (
          <ProductBackgroundOption
            key={backgroundOption}
            backgroundOption={backgroundOption}
            selectedBackground={background}
            selectedColorOption={selectedColorOption}
            onSelect={setBackground}
          />
        ))}
      </StyledMostlyFluidThumbs>
    </>
  )
}
